import React, { useEffect, useState } from 'react';

import CircularProgress from '~/components/core/Atomic/CircularProgress/CircularProgress';
import { useLoadingSpan } from '~/observability/useLoadingSpan';

import { useSorWaitingSince } from './sorClaimWrites';
import { SOR_REQUEST_TIMEOUT_MS } from './sorInterceptor';
import TEXTS from './texts';

// A shorter wait needs no words: the submit button's own busy state covers it.
const SHOW_AFTER_MS = 5_000;
const TICK_MS = 100;
// Outlasts the request it waits on, so the request's own timeout ends the span rather than the cap.
const SPAN_MAX_DURATION_MS = SOR_REQUEST_TIMEOUT_MS + 1_000;

/**
 * The claim page's account of a change waiting on the SoR: from 5 s after the earliest claim write still waiting was
 * sent, a strip at the top of the screen with a live count of the seconds it has waited. Fixed above MUI's modals
 * (z-index 1300), since most claim writes are sent from a dialog or popover, whose backdrop covers the claim bar. Not
 * dismissable: it goes when the wait ends.
 */
export const SorWaitingBanner: React.FC<{ sorName: string }> = ({ sorName }) => {
  const waitingSince = useSorWaitingSince();
  const [now, setNow] = useState(Date.now);
  useLoadingSpan({
    isLoading: waitingSince !== null,
    loadingSpanName: 'sor.commit',
    maxDurationMs: SPAN_MAX_DURATION_MS,
  });

  useEffect(() => {
    if (waitingSince === null) {
      return undefined;
    }
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(interval);
  }, [waitingSince]);

  if (waitingSince === null || now - waitingSince < SHOW_AFTER_MS) {
    return null;
  }
  const seconds = Math.floor((now - waitingSince) / 1000);
  return (
    <div
      role="status"
      className="fixed left-1/2 top-16 z-[1400] flex -translate-x-1/2 items-center gap-12 rounded-lg bg-grey-600 px-24 py-16 text-lg font-medium text-white shadow-xl"
    >
      <CircularProgress size={20} className="text-white" />
      <span>{TEXTS.saving(sorName)}</span>
      {/* Hidden from screen readers, so the status is announced once rather than on every tick. */}
      <span aria-hidden className="tabular-nums">
        {TEXTS.savingSeconds(seconds)}
      </span>
    </div>
  );
};
